import { useState, useMemo } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { apiCreateSiteContent, apiUpdateSiteContent } from '../../api';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { emitAdminToast } from '../../utils/adminToast';

// Pretty-print existing value for editing.
const formatInitialValue = (raw) => {
  if (raw == null) return '';
  if (typeof raw !== 'string') return JSON.stringify(raw, null, 2);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const AFFILIATE_FIELDS = [
  ['pageTitle', 'Page title', 'text'],
  ['pageSubtitle', 'Page subtitle', 'textarea'],
  ['ctaTitle', 'CTA heading', 'text'],
  ['ctaSubtitle', 'CTA description', 'textarea'],
  ['applyButtonLabel', 'Apply button label', 'text'],
  ['contactEmail', 'Contact email', 'email'],
  ['benefitsTitle', 'Benefits heading', 'text'],
  ['tiersTitle', 'Commission tiers heading', 'text'],
  ['eligibilityTitle', 'Eligibility heading', 'text'],
  ['eligibilityText', 'Who can join?', 'textarea'],
  ['payoutTitle', 'Payout heading', 'text'],
  ['payoutText', 'Payout terms', 'textarea'],
  ['qualificationTitle', 'Qualified-sale heading', 'text'],
  ['qualificationText', 'Qualified-sale terms', 'textarea'],
  ['cookieTitle', 'Cookie heading', 'text'],
  ['cookieText', 'Tracking cookie terms', 'textarea'],
];

const PAGE_META_FIELDS = [
  ['title', 'Page title', 'text'],
  ['subtitle', 'Page subtitle / last-updated text', 'textarea'],
  ['seoTitle', 'SEO title', 'text'],
  ['seoDescription', 'SEO description', 'textarea'],
];

const HOME_FIELDS = [
  ['bestsellersTitle', 'Bestsellers section title', 'text'],
  ['newReleasesTitle', 'New releases section title', 'text'],
  ['preorderTickerText', 'Pre-order announcement text', 'text'],
  ['preorderButtonLabel', 'Pre-order button label', 'text'],
  ['seoTitle', 'SEO title', 'text'],
  ['seoDescription', 'SEO description', 'textarea'],
  ['seoKeywords', 'SEO keywords', 'textarea'],
];

const SHOP_FIELDS = [
  ['allPricesLabel', 'All-prices label', 'text'],
  ['currencySymbol', 'Currency symbol', 'text'],
  ['firstBoundary', 'First price boundary', 'number'],
  ['secondBoundary', 'Second price boundary', 'number'],
  ['thirdBoundary', 'Third price boundary', 'number'],
];

const CONTACT_FIELDS = [
  ['whatsappEnabled', 'Enable WhatsApp button', 'checkbox'],
  ['whatsappUrl', 'WhatsApp URL', 'url'],
  ['whatsappLabel', 'WhatsApp accessible label', 'text'],
];

const TRUST_FIELDS = [
  ['icon', 'Icon name', 'text'],
  ['label', 'Short label', 'text'],
  ['title', 'Popup title', 'text'],
  ['desc', 'Popup description', 'textarea'],
  ['requiresReturnable', 'Only show for returnable products', 'checkbox'],
];

const isStructuredSection = (section) => section.endsWith('_meta')
  || ['home_settings', 'shop_filters', 'contact_settings', 'product_trust'].includes(section);

const fieldsForSection = (section) => {
  if (section === 'home_settings') return HOME_FIELDS;
  if (section === 'shop_filters') return SHOP_FIELDS;
  if (section === 'contact_settings') return CONTACT_FIELDS;
  if (section === 'product_trust') return TRUST_FIELDS;
  return PAGE_META_FIELDS;
};

const parseObject = (raw) => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const SiteContentForm = ({
  item,
  sections,
  allowedSections,
  defaultSection,
  onClose,
  onSaved,
}) => {
  const isEdit = !!item;
  const [form, setForm] = useState({
    section: item?.section || defaultSection || '',
    key: item?.key || (defaultSection ? 'content' : ''),
    valueText: useMemo(() => {
      if (item) return formatInitialValue(item.value);
      return '';
    }, [item]),
    position: item?.position ?? 0,
    isActive: item?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [affiliateSettings, setAffiliateSettings] = useState(() => parseObject(item?.value));
  const [structuredSettings, setStructuredSettings] = useState(() => parseObject(item?.value));
  const canClose = useUnsavedChanges(dirty && !saving);
  const requestClose = async () => {
    if (await canClose()) onClose();
  };

  const update = (k) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setDirty(true);
    setForm((prev) => ({ ...prev, [k]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.section.trim() || !form.key.trim()) {
      emitAdminToast('Section and key are required', 'error');
      return;
    }

    // Allow either valid JSON or a plain string for the value field.
    let value;
    if (form.section === 'affiliate_settings') value = affiliateSettings;
    else if (isStructuredSection(form.section)) value = structuredSettings;
    else {
      try {
        value = JSON.parse(form.valueText);
      } catch {
        value = form.valueText;
      }
    }

    const payload = {
      section: form.section.trim(),
      key: form.key.trim(),
      value, // backend will JSON-stringify objects/arrays
      position: Number(form.position) || 0,
      isActive: !!form.isActive,
    };

    setSaving(true);
    try {
      if (isEdit) await apiUpdateSiteContent(item.id, payload);
      else await apiCreateSiteContent(payload);
      onSaved();
    } catch { /* API client displays the error toast. */ } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-semibold text-black">
            {isEdit ? 'Edit Content Item' : 'New Content Item'}
          </h2>
          <button onClick={requestClose} className="text-black hover:text-black">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-sm">
          <div>
            <label className="block text-xs font-medium text-black mb-1">
              Section * <span className="text-black font-normal">(e.g. about_team)</span>
            </label>
            {allowedSections?.length ? (
              <select
                value={form.section}
                onChange={update('section')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                required
              >
                {allowedSections.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={form.section}
                onChange={update('section')}
                list="section-suggestions"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                required
              />
            )}
            <datalist id="section-suggestions">
              {(sections || []).map((section) => (
                <option key={section} value={section} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-xs font-medium text-black mb-1">Key *</label>
            <input
              type="text" value={form.key} onChange={update('key')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
              required
            />
          </div>

          {form.section === 'affiliate_settings' ? (
            <fieldset className="space-y-3 rounded-lg border border-orange-100 bg-orange-50/40 p-4">
              <legend className="px-1 text-sm font-semibold text-orange-700">Affiliate configuration</legend>
              {AFFILIATE_FIELDS.map(([key, label, type]) => (
                <label key={key} className="block text-xs font-medium text-black">
                  {label}
                  {type === 'textarea' ? (
                    <textarea
                      rows={3}
                      value={affiliateSettings[key] || ''}
                      onChange={(event) => {
                        setDirty(true);
                        setAffiliateSettings((current) => ({ ...current, [key]: event.target.value }));
                      }}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  ) : (
                    <input
                      type={type}
                      value={affiliateSettings[key] || ''}
                      onChange={(event) => {
                        setDirty(true);
                        setAffiliateSettings((current) => ({ ...current, [key]: event.target.value }));
                      }}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  )}
                </label>
              ))}
            </fieldset>
          ) : isStructuredSection(form.section) ? (
            <fieldset className="space-y-3 rounded-lg border border-orange-100 bg-orange-50/40 p-4">
              <legend className="px-1 text-sm font-semibold text-orange-700">
                {form.section.replaceAll('_', ' ')}
              </legend>
              {fieldsForSection(form.section).map(([key, label, type]) => (
                <label key={key} className="block text-xs font-medium text-black">
                  {label}
                  {type === 'checkbox' ? (
                    <input type="checkbox" checked={!!structuredSettings[key]} onChange={(event) => { setDirty(true); setStructuredSettings((current) => ({ ...current, [key]: event.target.checked })); }} className="ml-3" />
                  ) : type === 'textarea' ? (
                    <textarea rows={3} value={structuredSettings[key] || ''} onChange={(event) => { setDirty(true); setStructuredSettings((current) => ({ ...current, [key]: event.target.value })); }} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                  ) : (
                    <input type={type} value={structuredSettings[key] || ''} onChange={(event) => { setDirty(true); setStructuredSettings((current) => ({ ...current, [key]: event.target.value })); }} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                  )}
                </label>
              ))}
            </fieldset>
          ) : (
            <div>
              <label className="block text-xs font-medium text-black mb-1">Content</label>
              <textarea
                rows={20}
                value={form.valueText}
                onChange={update('valueText')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs"
                placeholder={'[\n  { "heading": "Title 1", "paragraph": "Paragraph 1" },\n  { "heading": "Title 2", "paragraph": "Paragraph 2" }\n]'}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-black mb-1">Position</label>
              <input
                type="number" min="0" value={form.position} onChange={update('position')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <label className="inline-flex items-center gap-2 pb-2">
              <input
                type="checkbox" checked={!!form.isActive} onChange={update('isActive')}
              />
              <span className="text-sm text-black">Active</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button
              type="button" onClick={requestClose}
              className="px-4 py-2 text-sm text-black hover:text-black"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              <FiSave size={16} />
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteContentForm;
